import React, { ReactNode, useMemo, useState } from "react";
import { Alert, Table, Button } from "react-bootstrap";
import { ChevronDown, ChevronRight } from "react-bootstrap-icons";
import styled from "styled-components";

import { formatHumanDuration } from "../../utils/analytics";
import Branding from "../../config/Branding";

type ContentBrandTreeTableProps = {
    data: Record<string, any>[];
    emptyMessage: string;
};

type TreeNode = {
    id: string;
    label: string;
    level: 0 | 1 | 2 | 3;
    metricsRow: Record<string, any> | null;
    children: TreeNode[];
};

const HIERARCHY_KEYS = ["brand_name", "channel_type", "channel", "user_country"];
const INDENT_BY_LEVEL = [0, 18, 36, 54];

const TableContainer = styled.div`
    overflow-x: hidden;
    overflow-y: auto;
    max-height: 75vh;
`;

const StyledTreeTable = styled(Table)`
    border-collapse: separate;
    border-spacing: 0;
    margin-bottom: 0;
    width: 100%;
    table-layout: fixed;

    th, td {
        border: thin solid #282828;
        padding: 6px 8px;
        text-align: center;
        color: ${Branding.white};
        vertical-align: middle !important;
    }

    th {
        font-size: 0.8rem;
        line-height: 1.2;
        white-space: normal;
        word-break: break-word;
    }

    td {
        font-size: 0.82rem;
    }
`;

const StickyHeader = styled.thead`
    position: sticky;
    top: 0;
    z-index: 10;
`;

const ContentBrandTreeTable: React.FC<ContentBrandTreeTableProps> = ({ data, emptyMessage }) => {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    const toNumber = (value: number | string | null | undefined): number | null => {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    };

    const isPercentKey = (columnKey: string): boolean => /(_pct$|percent|percentage)/i.test(columnKey);
    const isPopPctKey = (columnKey: string): boolean => /_pop_pct$/i.test(columnKey);
    const isAvgPercentWatchedKey = (columnKey: string): boolean => /avg_?percent_?watched/i.test(columnKey);
    const isDurationKey = (columnKey: string): boolean => /hours?_watched|avg_hours_watched|watch[_ ]?time|watch_time|total_watch_time_in_seconds/i.test(columnKey);
    const getDurationUnit = (columnKey: string): "hours" | "seconds" => {
        if (/hours?_watched|avg_hours_watched/i.test(columnKey)) {
            return "hours";
        }

        return "seconds";
    };

    const formatValue = (value: any, columnKey?: string): ReactNode => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }

        if (columnKey && isDurationKey(columnKey)) {
            const parsed = toNumber(value);
            if (parsed === null) {
                return "-";
            }

            return formatHumanDuration(parsed, getDurationUnit(columnKey));
        }

        if (typeof value === "number") {
            if (!Number.isFinite(value)) {
                return "-";
            }

            if (columnKey && isPercentKey(columnKey)) {
                return `${value.toFixed(2)}%`;
            }

            if (Number.isInteger(value)) {
                return value.toLocaleString();
            }

            return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        }

        if (typeof value === "boolean") {
            return value ? "Yes" : "No";
        }

        return String(value);
    };

    const getPopValue = (value: any, columnKey: string): ReactNode => {
        const parsed = toNumber(value);
        if (parsed === null) {
            return "-";
        }

        let background = "rgba(251, 184, 1, 0.35)";
        if (parsed > 0) {
            background = "rgba(182, 255, 0, 0.35)";
        } else if (parsed < 0) {
            background = "rgba(255, 58, 16, 0.35)";
        }

        return (
            <span
                style={{
                    background,
                    color: Branding.white,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    display: "inline-block",
                    minWidth: "52px"
                }}
            >
                {formatValue(parsed, columnKey)}
            </span>
        );
    };

    const getAvgPercentWatchedValue = (value: any, columnKey: string): ReactNode => {
        const parsed = toNumber(value);
        if (parsed === null) {
            return "-";
        }

        const normalized = Math.max(0, Math.min(1, parsed / 100));
        const dark = { r: 15, g: 23, b: 42 };
        const brightBlue = { r: 0, g: 183, b: 255 };
        const r = Math.round(dark.r + ((brightBlue.r - dark.r) * normalized));
        const g = Math.round(dark.g + ((brightBlue.g - dark.g) * normalized));
        const b = Math.round(dark.b + ((brightBlue.b - dark.b) * normalized));
        const background = `rgb(${r}, ${g}, ${b})`;

        return (
            <span
                style={{
                    background,
                    color: Branding.white,
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontWeight: 700,
                    display: "inline-block",
                    minWidth: "56px"
                }}
            >
                {formatValue(parsed, columnKey)}
            </span>
        );
    };

    const getHeaderLabel = (column: string): ReactNode => {
        const label = column.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
        const words = label.split(" ");
        const splitIndex = Math.ceil(words.length / 2);
        const firstLine = words.slice(0, splitIndex).join(" ");
        const secondLine = words.slice(splitIndex).join(" ");

        return (
            <div>
                <div>{firstLine}</div>
                {secondLine && <div>{secondLine}</div>}
            </div>
        );
    };

    const metricColumns = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) {
            return [] as string[];
        }

        const columns: string[] = [];
        data.forEach(row => {
            Object.keys(row).forEach(key => {
                if (!HIERARCHY_KEYS.includes(key) && !columns.includes(key)) {
                    columns.push(key);
                }
            });
        });

        return columns;
    }, [data]);

    const treeRoots = useMemo<TreeNode[]>(() => {
        const brandMap = new Map<string, TreeNode>();
        const normalize = (value: string): string => value.trim().replace(/\s+/g, " ").toLowerCase();
        const isAll = (value: string): boolean => normalize(value) === "all";
        const rowsForTree = [...data].sort((a, b) => {
            const getSpecificity = (row: Record<string, any>): number => {
                let score = 0;
                if (!isAll(String(row.channel_type ?? "ALL"))) score += 1;
                if (!isAll(String(row.channel ?? "ALL"))) score += 1;
                if (!isAll(String(row.user_country ?? "ALL"))) score += 1;
                return score;
            };

            return getSpecificity(a) - getSpecificity(b);
        });

        rowsForTree.forEach(row => {
            const brandName = String(row.brand_name ?? "Unknown Brand");
            const channelType = String(row.channel_type ?? "ALL");
            const channel = String(row.channel ?? "ALL");
            const userCountry = String(row.user_country ?? "ALL");

            let brandNode = brandMap.get(brandName);
            if (!brandNode) {
                brandNode = {
                    id: `brand:${brandName}`,
                    label: brandName,
                    level: 0,
                    metricsRow: null,
                    children: []
                };
                brandMap.set(brandName, brandNode);
            }

            if (isAll(channelType)) {
                brandNode.metricsRow = row;
                return;
            }

            let typeNode = brandNode.children.find(node => node.label === channelType);
            if (!typeNode) {
                typeNode = {
                    id: `type:${brandName}:${channelType}`,
                    label: channelType,
                    level: 1,
                    metricsRow: null,
                    children: []
                };
                brandNode.children.push(typeNode);
            }
            if (!typeNode) {
                return;
            }

            if (isAll(channel)) {
                typeNode.metricsRow = row;
                return;
            }

            let channelNode = typeNode.children.find(node => node.label === channel);
            if (!channelNode) {
                channelNode = {
                    id: `channel:${brandName}:${channelType}:${channel}`,
                    label: channel,
                    level: 2,
                    metricsRow: null,
                    children: []
                };
                typeNode.children.push(channelNode);
            }

            if (isAll(userCountry)) {
                channelNode.metricsRow = row;
                return;
            }

            let countryNode = channelNode.children.find(node => node.label === userCountry);
            if (!countryNode) {
                countryNode = {
                    id: `country:${brandName}:${channelType}:${channel}:${userCountry}`,
                    label: userCountry,
                    level: 3,
                    metricsRow: null,
                    children: []
                };
                channelNode.children.push(countryNode);
            }

            countryNode.metricsRow = row;
        });

        const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
            return nodes
                .sort((a, b) => a.label.localeCompare(b.label))
                .map(node => ({
                    ...node,
                    children: sortNodes(node.children)
                }));
        };

        return sortNodes(Array.from(brandMap.values()));
    }, [data]);

    const visibleNodes = useMemo(() => {
        const rows: TreeNode[] = [];

        const walk = (nodes: TreeNode[]): void => {
            nodes.forEach(node => {
                rows.push(node);
                if (expandedIds.has(node.id) && node.children.length > 0) {
                    walk(node.children);
                }
            });
        };

        walk(treeRoots);
        return rows;
    }, [treeRoots, expandedIds]);

    const toggleNode = (nodeId: string): void => {
        setExpandedIds(prev => {
            const updated = new Set(prev);
            if (updated.has(nodeId)) {
                updated.delete(nodeId);
            } else {
                updated.add(nodeId);
            }

            return updated;
        });
    };

    if (!Array.isArray(data) || data.length === 0) {
        return <Alert variant="info" style={{ marginBottom: 0 }}>{emptyMessage}</Alert>;
    }

    const getLevelBackground = (level: TreeNode["level"], rowIndex: number): string => {
        const odd = rowIndex % 2 !== 0;

        if (level === 0) {
            return odd ? "#1f2d47" : "#243654";
        }

        if (level === 1) {
            return odd ? "#1a2840" : "#1e304a";
        }

        if (level === 2) {
            return odd ? "#152238" : "#192943";
        }

        return odd ? "#111c2f" : "#152339";
    };

    const getHoverBackground = (level: TreeNode["level"]): string => {
        if (level === 0) {
            return "#2b4165";
        }

        if (level === 1) {
            return "#243854";
        }

        if (level === 2) {
            return "#1f314b";
        }

        return "#1a2a41";
    };

    return (
        <TableContainer>
            <StyledTreeTable bordered variant="dark">
                <StickyHeader>
                    <tr>
                        <th style={{ width: "230px", minWidth: "230px", background: Branding.superDarkBackground }}>Brand / Type / Channel / Country</th>
                        {metricColumns.map(column => (
                            <th key={column} style={{ width: "86px", minWidth: "86px", background: Branding.superDarkBackground }}>
                                {getHeaderLabel(column)}
                            </th>
                        ))}
                    </tr>
                </StickyHeader>
                <tbody>
                    {visibleNodes.map((node, index) => {
                        const hasChildren = node.children.length > 0;
                        const isExpanded = expandedIds.has(node.id);
                        const metricsRow = node.metricsRow || {};
                        const rowBackground = getLevelBackground(node.level, index);
                        const rowHoverBackground = getHoverBackground(node.level);

                        return (
                            <tr
                                key={node.id}
                                style={{ backgroundColor: rowBackground }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHoverBackground; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = rowBackground; }}
                            >
                                <td style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            marginLeft: `${INDENT_BY_LEVEL[node.level]}px`,
                                            cursor: hasChildren ? "pointer" : "default"
                                        }}
                                        onClick={() => {
                                            if (hasChildren) {
                                                toggleNode(node.id);
                                            }
                                        }}
                                        onKeyDown={e => {
                                            if (hasChildren && (e.key === "Enter" || e.key === " ")) {
                                                e.preventDefault();
                                                toggleNode(node.id);
                                            }
                                        }}
                                        role={hasChildren ? "button" : undefined}
                                        tabIndex={hasChildren ? 0 : -1}
                                        aria-label={hasChildren ? `${isExpanded ? "Collapse" : "Expand"} ${node.label}` : undefined}
                                    >
                                        {hasChildren ? (
                                            <Button
                                                size="sm"
                                                variant="link"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleNode(node.id);
                                                }}
                                                style={{ padding: "0 6px 0 0", color: "#f8fafc", textDecoration: "none" }}
                                                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                                            >
                                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </Button>
                                        ) : (
                                            <span style={{ width: "20px", display: "inline-block", flexShrink: 0 }} />
                                        )}
                                        <span
                                            title={node.label}
                                            style={{
                                                fontWeight: node.level === 0 ? 700 : 500,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                display: "block",
                                                flex: 1,
                                                minWidth: 0
                                            }}
                                        >
                                            {node.label}
                                        </span>
                                    </div>
                                </td>
                                {metricColumns.map(column => (
                                    <td key={`${node.id}:${column}`}>
                                        {isPopPctKey(column)
                                            ? getPopValue(metricsRow[column], column)
                                            : isAvgPercentWatchedKey(column)
                                                ? getAvgPercentWatchedValue(metricsRow[column], column)
                                            : formatValue(metricsRow[column], column)}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </StyledTreeTable>
        </TableContainer>
    );
};

export default ContentBrandTreeTable;
